import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import { Search, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Close } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { vendorInvoiceService } from '../services/vendorInvoiceService';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { shipmentsInService } from '../services/shipmentsInService';
import { formatCurrency } from '../components/CurrencyTextField';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { printWithFilename } from '../utils/printUtils';
import { ProcurementSummaryBar, ProcurementSummaryField } from '../components/procurement/ProcurementSummaryBar';
import { formatDateOnly, formatLocalTodayYmd, sortTimestampFromDateOnly } from '../utils/dateUtils';

const emptyInvoiceForm = () => ({
  invoice_number: '',
  purchase_order_id: '',
  receiver_id: '',
  invoice_date: formatLocalTodayYmd(),
  total_amount: '',
  status: 'open',
  notes: ''
});

const ProcurementInvoices = () => {
  const DEFAULT_COL_WIDTHS = { invNo: 140, vendor: 220, poNo: 130, receiver: 130, date: 120, amount: 120, status: 120 };
  const [searchParams, setSearchParams] = useSearchParams();
  const poFilter = searchParams.get('po');
  const openNew = searchParams.get('openNew') === '1';
  const invoiceFilter = searchParams.get('invoice');
  const printRequested = searchParams.get('print') === '1';
  const [searchTerm, setSearchTerm] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState(emptyInvoiceForm);
  const [saving, setSaving] = useState(false);
  const [nextNoLoading, setNextNoLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [receiversByPo, setReceiversByPo] = useState([]);
  const hasAutoPrintedRef = useRef(false);
  const [sortKey, setSortKey] = useState('invoice_number');
  const [sortDirection, setSortDirection] = useState('desc');
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_procurement_invoices_col_widths',
    minWidths: { invNo: 110, vendor: 150, poNo: 100, receiver: 100, date: 100, amount: 100, status: 100 }
  });

  const handleSort = (key) => {
    setSortDirection((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const displayedInvoices = useMemo(() => {
    const list = [...invoices];
    const getter = (row) => {
      switch (sortKey) {
        case 'invoice_number': return row.identifier ?? row.invoice_number ?? '';
        case 'vendor': return row.vendor_name ?? '';
        case 'po_number': return row.po_identifier ?? row.po_number ?? '';
        case 'receiver_no': return row.receiver_identifier ?? row.receiver_no ?? '';
        case 'invoice_date': return sortTimestampFromDateOnly(row.invoice_date);
        case 'total_amount': return Number(row.total_amount) || 0;
        case 'status': return row.status ?? '';
        default: return '';
      }
    };
    const dir = sortDirection === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }, [invoices, sortKey, sortDirection]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await vendorInvoiceService.getAll({
        search: searchTerm || undefined,
        purchase_order_id: poFilter || undefined,
        limit: 100
      });
      const list = res.data ?? [];
      setInvoices(list);
      if (invoiceFilter) {
        const matched = list.find((inv) => String(inv.id) === String(invoiceFilter));
        setSelectedInvoice(matched || (list.length > 0 ? list[0] : null));
      } else {
        setSelectedInvoice(list.length > 0 ? list[0] : null);
      }
    } catch (err) {
      console.error('Error loading vendor invoices:', err);
      setError(err.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [poFilter, invoiceFilter]);

  // When navigating from PO with ?po=X&openNew=1, open New Invoice dialog with PO pre-selected
  useEffect(() => {
    if (openNew && poFilter) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('openNew');
        return next;
      });
      openNewDialog(poFilter);
    }
  }, [openNew, poFilter]);

  useEffect(() => {
    if (!printRequested) return;
    if (loading) return;
    if (!selectedInvoice) return;
    if (hasAutoPrintedRef.current) return;
    hasAutoPrintedRef.current = true;
    const invoiceFilename = selectedInvoice?.identifier || selectedInvoice?.invoice_number || 'Vendor-Invoice';
    printWithFilename(invoiceFilename, { delayMs: 350 });
  }, [printRequested, loading, selectedInvoice]);

  useEffect(() => {
    if (!printRequested) hasAutoPrintedRef.current = false;
  }, [printRequested]);

  const loadPOsAndReceivers = async () => {
    try {
      const [poRes, recRes] = await Promise.all([
        purchaseOrderService.getAll({ limit: 500 }),
        shipmentsInService.getAll({ limit: 500 })
      ]);
      setPurchaseOrders(poRes?.data ?? []);
      setReceiversByPo(recRes?.data ?? []);
    } catch (err) {
      console.error('Error loading POs/receivers:', err);
    }
  };

  const handleSearch = () => {
    loadInvoices();
  };

  const openNewDialog = async (preselectedPoId = null) => {
    setInvoiceForm(emptyInvoiceForm());
    setNewDialogOpen(true);
    setNextNoLoading(true);
    setError('');
    await loadPOsAndReceivers();
    try {
      const res = await vendorInvoiceService.getNextNumber();
      const next = res?.data?.invoice_number ?? `INV-${Date.now().toString(36).slice(-5).toUpperCase()}`;
      setInvoiceForm((f) => ({
        ...f,
        invoice_number: next,
        invoice_date: formatLocalTodayYmd(),
        purchase_order_id: preselectedPoId ? Number(preselectedPoId) : ''
      }));
    } catch (err) {
      setInvoiceForm((f) => ({
        ...f,
        invoice_date: formatLocalTodayYmd(),
        purchase_order_id: preselectedPoId ? Number(preselectedPoId) : ''
      }));
    } finally {
      setNextNoLoading(false);
    }
  };

  const handleCreateInvoice = async () => {
    const { invoice_number, purchase_order_id, invoice_date } = invoiceForm;
    if (!invoice_number?.trim()) {
      setError('Invoice number is required.');
      return;
    }
    if (!purchase_order_id) {
      setError('Purchase order is required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        invoice_number: invoice_number.trim(),
        purchase_order_id: Number(purchase_order_id),
        receiver_id: invoiceForm.receiver_id ? Number(invoiceForm.receiver_id) : null,
        invoice_date: invoice_date || formatLocalTodayYmd(),
        total_amount: invoiceForm.total_amount !== '' && invoiceForm.total_amount != null ? Number(invoiceForm.total_amount) : null,
        status: invoiceForm.status || 'open',
        notes: invoiceForm.notes || null
      };
      await vendorInvoiceService.create(payload);
      setNewDialogOpen(false);
      loadInvoices();
    } catch (err) {
      console.error('Error creating invoice:', err);
      setError(err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = async (row) => {
    if (!row) return;
    setSelectedInvoice(row);
    setEditDialogOpen(true);
    setError('');
    await loadPOsAndReceivers();
    setInvoiceForm({
      invoice_number: row.invoice_number ?? '',
      purchase_order_id: row.purchase_order_id ?? '',
      receiver_id: row.receiver_id ?? '',
      invoice_date: (row.invoice_date || '').toString().slice(0, 10) || formatLocalTodayYmd(),
      total_amount: row.total_amount ?? '',
      status: row.status ?? 'open',
      notes: row.notes ?? ''
    });
  };

  const handleUpdateInvoice = async () => {
    if (!selectedInvoice?.id) return;
    const { invoice_number, purchase_order_id, invoice_date } = invoiceForm;
    if (!invoice_number?.trim()) {
      setError('Invoice number is required.');
      return;
    }
    if (!purchase_order_id) {
      setError('Purchase order is required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        invoice_number: invoice_number.trim(),
        purchase_order_id: Number(purchase_order_id),
        receiver_id: invoiceForm.receiver_id ? Number(invoiceForm.receiver_id) : null,
        invoice_date: invoice_date || formatLocalTodayYmd(),
        total_amount: invoiceForm.total_amount !== '' && invoiceForm.total_amount != null ? Number(invoiceForm.total_amount) : null,
        status: invoiceForm.status || 'open',
        notes: invoiceForm.notes || null
      };
      await vendorInvoiceService.update(selectedInvoice.id, payload);
      setEditDialogOpen(false);
      setSelectedInvoice(null);
      loadInvoices();
    } catch (err) {
      console.error('Error updating invoice:', err);
      setError(err.response?.data?.message || 'Failed to update invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInvoice = async () => {
    if (!selectedInvoice?.id) return;
    try {
      setSaving(true);
      setError('');
      await vendorInvoiceService.delete(selectedInvoice.id);
      setDeleteConfirmOpen(false);
      setSelectedInvoice(null);
      loadInvoices();
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError(err.response?.data?.message || 'Failed to delete invoice');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (v) => formatDateOnly(v, 'MM/dd/yyyy') || '—';

  const receiversForSelectedPo = invoiceForm.purchase_order_id
    ? receiversByPo.filter((r) => Number(r.purchase_order_id) === Number(invoiceForm.purchase_order_id))
    : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Vendor Invoices
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="primary" size="small" startIcon={<EditIcon />} onClick={() => selectedInvoice && openEditDialog(selectedInvoice)} disabled={!selectedInvoice}>
            Edit
          </Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => selectedInvoice && setDeleteConfirmOpen(true)} disabled={!selectedInvoice}>
            Delete
          </Button>
          <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={openNewDialog}>
            New
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {poFilter && (
        <Alert severity="info" sx={{ mb: 1.5 }} onClose={() => setSearchParams({})}>
          Showing invoices for P.O. #{poFilter}.{' '}
          <Button size="small" onClick={() => setSearchParams({})}>Clear filter</Button>
        </Alert>
      )}

      <ProcurementSummaryBar>
        <ProcurementSummaryField label="Invoice #">
          <Box sx={{ fontWeight: 600 }}>{selectedInvoice?.identifier ?? selectedInvoice?.invoice_number ?? '—'}</Box>
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Vendor">{selectedInvoice?.vendor_name ?? '—'}</ProcurementSummaryField>
        <ProcurementSummaryField label="P.O. #">
          {selectedInvoice?.po_identifier ?? selectedInvoice?.po_number ?? selectedInvoice?.purchase_order_id ?? '—'}
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Receiver">
          {selectedInvoice?.receiver_identifier ?? selectedInvoice?.receiver_no ?? (selectedInvoice?.receiver_id ? `#${selectedInvoice.receiver_id}` : '—')}
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Date">
          {selectedInvoice ? formatDate(selectedInvoice.invoice_date) : '—'}
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Amount">
          {selectedInvoice ? formatCurrency(selectedInvoice.total_amount) : '—'}
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Status">
          <Box sx={{ textTransform: 'capitalize' }}>{selectedInvoice?.status ?? '—'}</Box>
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Notes" wide>
          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedInvoice?.notes ?? '—'}</Box>
        </ProcurementSummaryField>
        <IconButton
          size="small"
          onClick={() => setSelectedInvoice(null)}
          disabled={!selectedInvoice}
          sx={{ ml: 'auto' }}
          title="Clear selection"
        >
          <Close fontSize="small" />
        </IconButton>
      </ProcurementSummaryBar>

      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by invoice, vendor, or P.O. number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" onClick={handleSearch}>
                    <Search />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 260 }}
          />
          <Button variant="outlined" size="small" onClick={handleSearch}>
            Search
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <ResizableHeaderCell width={colWidths.invNo} onResizeStart={(e) => startResize('invNo', e)} sortable sortActive={sortKey === 'invoice_number'} sortDirection={sortDirection} onSort={() => handleSort('invoice_number')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Invoice #</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.vendor} onResizeStart={(e) => startResize('vendor', e)} sortable sortActive={sortKey === 'vendor'} sortDirection={sortDirection} onSort={() => handleSort('vendor')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Vendor</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.poNo} onResizeStart={(e) => startResize('poNo', e)} sortable sortActive={sortKey === 'po_number'} sortDirection={sortDirection} onSort={() => handleSort('po_number')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>P.O. #</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.receiver} onResizeStart={(e) => startResize('receiver', e)} sortable sortActive={sortKey === 'receiver_no'} sortDirection={sortDirection} onSort={() => handleSort('receiver_no')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Receiver</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.date} onResizeStart={(e) => startResize('date', e)} sortable sortActive={sortKey === 'invoice_date'} sortDirection={sortDirection} onSort={() => handleSort('invoice_date')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Date</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.amount} onResizeStart={(e) => startResize('amount', e)} align="right" sortable sortActive={sortKey === 'total_amount'} sortDirection={sortDirection} onSort={() => handleSort('total_amount')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Amount</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.status} onResizeStart={(e) => startResize('status', e)} sortable sortActive={sortKey === 'status'} sortDirection={sortDirection} onSort={() => handleSort('status')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Status</Typography></ResizableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No vendor invoices. Create one from Procurement → Invoices and link to a P.O. (and optionally a receiver) to mark receiving as billed.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayedInvoices.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={selectedInvoice?.id === row.id}
                  onClick={() => setSelectedInvoice(row)}
                  onDoubleClick={() => openEditDialog(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ width: colWidths.invNo }}>{row.identifier ?? row.invoice_number ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.vendor }}>{row.vendor_name ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.poNo }}>{row.po_identifier ?? row.po_number ?? row.purchase_order_id ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.receiver }}>{row.receiver_identifier ?? row.receiver_no ?? (row.receiver_id ? `#${row.receiver_id}` : '—')}</TableCell>
                  <TableCell sx={{ width: colWidths.date }}>{formatDate(row.invoice_date)}</TableCell>
                  <TableCell sx={{ width: colWidths.amount }} align="right">{formatCurrency(row.total_amount)}</TableCell>
                  <TableCell sx={{ width: colWidths.status }}>{row.status ?? '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* New invoice dialog */}
      <Dialog open={newDialogOpen} onClose={() => !saving && setNewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Vendor Invoice</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Invoice number"
                value={invoiceForm.invoice_number}
                onChange={(e) => setInvoiceForm((f) => ({ ...f, invoice_number: e.target.value }))}
                required
                disabled={nextNoLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Invoice date" type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm((f) => ({ ...f, invoice_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Purchase order</InputLabel>
                <Select
                  label="Purchase order"
                  value={invoiceForm.purchase_order_id || ''}
                  onChange={(e) => setInvoiceForm((f) => ({ ...f, purchase_order_id: e.target.value, receiver_id: '' }))}
                >
                  <MenuItem value="">—</MenuItem>
                  {purchaseOrders.map((po) => (
                    <MenuItem key={po.id} value={po.id}>
                      {po.po_number} — {po.vendor_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Receiver (link to mark billed)</InputLabel>
                <Select
                  label="Receiver (link to mark billed)"
                  value={invoiceForm.receiver_id || ''}
                  onChange={(e) => setInvoiceForm((f) => ({ ...f, receiver_id: e.target.value }))}
                >
                  <MenuItem value="">—</MenuItem>
                  {receiversForSelectedPo.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.identifier ?? r.receiver_no} — {r.receiver_date ? formatDate(r.receiver_date) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Total amount" type="number" inputProps={{ min: 0, step: 0.01 }} value={invoiceForm.total_amount ?? ''} onChange={(e) => setInvoiceForm((f) => ({ ...f, total_amount: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={invoiceForm.status} onChange={(e) => setInvoiceForm((f) => ({ ...f, status: e.target.value }))}>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" multiline rows={2} value={invoiceForm.notes} onChange={(e) => setInvoiceForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateInvoice} disabled={saving || nextNoLoading}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit invoice dialog */}
      <Dialog open={editDialogOpen} onClose={() => !saving && (setEditDialogOpen(false), setSelectedInvoice(null))} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Vendor Invoice</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Invoice number" value={invoiceForm.invoice_number} onChange={(e) => setInvoiceForm((f) => ({ ...f, invoice_number: e.target.value }))} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Invoice date" type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm((f) => ({ ...f, invoice_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Purchase order</InputLabel>
                <Select label="Purchase order" value={invoiceForm.purchase_order_id || ''} onChange={(e) => setInvoiceForm((f) => ({ ...f, purchase_order_id: e.target.value, receiver_id: '' }))}>
                  <MenuItem value="">—</MenuItem>
                  {purchaseOrders.map((po) => (
                    <MenuItem key={po.id} value={po.id}>
                      {po.po_number} — {po.vendor_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Receiver (link to mark billed)</InputLabel>
                <Select label="Receiver (link to mark billed)" value={invoiceForm.receiver_id || ''} onChange={(e) => setInvoiceForm((f) => ({ ...f, receiver_id: e.target.value }))}>
                  <MenuItem value="">—</MenuItem>
                  {receiversForSelectedPo.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.identifier ?? r.receiver_no} — {r.receiver_date ? formatDate(r.receiver_date) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Total amount" type="number" inputProps={{ min: 0, step: 0.01 }} value={invoiceForm.total_amount ?? ''} onChange={(e) => setInvoiceForm((f) => ({ ...f, total_amount: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={invoiceForm.status} onChange={(e) => setInvoiceForm((f) => ({ ...f, status: e.target.value }))}>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" multiline rows={2} value={invoiceForm.notes} onChange={(e) => setInvoiceForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setSelectedInvoice(null); }} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateInvoice} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteConfirmOpen} onClose={() => !saving && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Vendor Invoice</DialogTitle>
        <DialogContent>
          <Typography>
            Delete invoice <strong>{selectedInvoice?.identifier ?? selectedInvoice?.invoice_number ?? selectedInvoice?.id}</strong>? If linked to a receiver, that receiver will be marked not billed. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteInvoice} disabled={saving}>
            {saving ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcurementInvoices;
